import { CompareStatus, qaCompareSimpleValue } from 'pages/QA/components/QACardDetails/utils';
import { useEffect, useState } from 'react';
import { Button, Collapse, Form, InputGroup, Row, Col } from 'react-bootstrap';
import CompareIcon from '../../../CompareIcon';
import Style from '../../index.module.css';
import { IcebergIcon, useLocalStorage } from '@tradesolution/iceberg-ui-react';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';

interface Props {
  value: string | undefined;
  onChange: (value: string) => void;
  changeRequest: {
    kontonummer: string;
    kontonummerFromCr: string;
  };
  error?: string;
}

const KontonummerRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(props.changeRequest.kontonummer, props.value),
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);

  const handleExpandRowClick = () => {
    setIsExpandRow(!isExpandRow);
  };

  const transferKontonummer = (kontonummer: string) => {
    // If the value is not already set, update it
    if (props.value !== kontonummer) {
      props.onChange(kontonummer);
    }
  };

  // Automatically expand the accordion if the values are not alike
  useEffect(() => {
    if (compareStatus === 'ulik' || compareStatus === 'ny') {
      setIsExpandRow(true);
    } else {
      setIsExpandRow(false);
    }
  }, [compareStatus]);

  useEffect(() => {
    setCompareStatus(qaCompareSimpleValue(props.changeRequest.kontonummer, props.value));
  }, [props.value, props.changeRequest.kontonummer]);

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequest.kontonummerFromCr) {
      const error = ChangeRequestValidator.hasKontonummerErrors(
        props.changeRequest.kontonummerFromCr,
      );
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequest.kontonummerFromCr]);

  return (
    <>
      <div className={Style.changeRequestRowHeaderContainer} onClick={handleExpandRowClick}>
        <h6 className={Style.changeRequestRowHeader}>
          {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
          <span className={Style.changeRequestRowHeaderText}>Kontonummer</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.changeRequest.kontonummer || ''}
                    readOnly
                    isValid={!crValueError && !!props.changeRequest.kontonummerFromCr}
                    isInvalid={!!crValueError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.changeRequest.kontonummerFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Kontonummer er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon
                  compareStatus={compareStatus}
                  onClick={() => transferKontonummer(props.changeRequest.kontonummerFromCr)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    type="text"
                    value={props.value}
                    onChange={(e) => props.onChange(e.target.value.trim())}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.changeRequest.kontonummerFromCr} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Kontonummer er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
                {props.error && <Form.Text className="text-danger">{props.error}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};
export default KontonummerRow;
